/** Shared dynamic → legacy KPI hydration (mirrors SI/Backend/src/util/hydrateReportFromDynamicKpis.js) */

export const DYNAMIC_TO_LEGACY_FIELD_MAP = {
  B1: {
    scope1Emissions: ["scope1", "scope_1_emission"],
    scope2Emissions: ["scope2", "scope_2_emission"],
    grossRevenue: ["gross_revenue", "grossRevenue"],
  },
  B2: { totalWaterConsumption: ["total_water_consumption"] },
  B3: { totalHazardousWaste: ["total_hazardous_waste_produced"] },
  B4: { totalNonHazardousWaste: ["non_hazardous_waste"] },
  B5: {
    Y1: ["revenue_allocated_2022"],
    Y2: ["revenue_allocated_2023"],
    Y3: ["revenue_allocated_2024"],
  },
  B6: {
    totalElectricityConsumption: ["total_electricity_consumption"],
    electricityFromGrid: ["total_electricity_consumption_grid"],
    recPurchased: ["total_electricity_consumption_purchased_renewable"],
    electricityFromRenewables: ["total_electricity_consumption_renewable"],
  },
  E1: {
    scope1Emissions: ["scope_1_emission", "scope1"],
    scope2Emissions: ["scope_2_emission", "scope2"],
  },
  E2: { scope3Emissions: ["scope_3_emission"] },
  E3: { waterConsumed: ["total_water_consumption"] },
  E4: { wasteToLandfill: ["waste_disposed_to_landfill"] },
  E5: { noxReleased: ["nox_released_in_environment"] },
  E6: { soxReleased: ["sox_released_in_environment"] },
  E7: { h2sConcentration: ["h2s_concentration"] },
  E8: { noiseLevel: ["la90_noise_level"] },
  E9: {
    wasteWaterTreated: ["total_waste_water_before_disposal"],
    totalWasteWater: ["total_waste_generated"],
  },
  S1: {
    totalMen: ["total_men_in_workforce"],
    totalWomen: ["total_women_in_workforce", "total_women_in_workforce "],
    totalNonBinary: ["total_non_binary_in_workforce"],
  },
  S2: { foreignEmployees: ["total_foreign_in_workforce"] },
  S3: {
    directEmployees: ["num_direct_employees"],
    indirectEmployees: ["num_indirect_employees"],
  },
  S4: { averageTenure: ["avg_tenure_employees"] },
  S5: { trainingHours: ["avg_num_hours_training_employees"] },
  S6: {
    nonDiscriminatoryPolicy: [
      "non_discriminatory_policy",
      "equal_opportunity_nondiscrimination_policy_exists",
      "non_discrimination_policy_covering_staff_and_service_delivery",
      // Legacy FormSchema wrongly used S5's training-hours key for S6
      "avg_num_hours_training_employees",
    ],
  },
  S7: { trir: ["trir"] },
  S8: { campusHires: ["total_num_hired_campus_placement"] },
  G1: { taxReliefReceived: ["tax_relief_subsidies"] },
  G2: { pensionContribution: ["per_pension_salary"] },
  G3: { localSuppliersSpending: ["amount_spent_local_suppliers"] },
  G4: { newSuppliersSpending: ["amount_spent_suppliers_less_than_five_yrs"] },
  G5: { localCommunitiesSpending: ["amount_spent_local_communities"] },
  G6: {
    innovativeTechnologiesSpending: ["amount_spent_innovative_technologies"],
  },
  G7: { ethicsPolicyInPlace: ["ethics_policy"] },
  G8: {
    totalComplaints: ["total_complaints"],
    resolvedComplaints: ["total_complaints_resolved"],
  },
  G9: { boardMembers: ["num_board_members"] },
  G10: { csrSpending: ["amount_spent_csr"] },
};

const KPI_CODE_TO_SECTION = {
  B: "basicSectorSpecificKPI",
  E: "environmentKPI",
  S: "socialKPI",
  G: "governanceKPI",
};

const BOOLEAN_LEGACY_FIELDS = new Set([
  "taxReliefReceived",
  "ethicsPolicyInPlace",
  "nonDiscriminatoryPolicy",
]);

export function parseLegacyKpiCode(schemaCode) {
  if (!schemaCode) return null;
  const match = String(schemaCode).match(/([BESG]\d+)$/i);
  return match ? match[1].toUpperCase() : null;
}

/** Resolve legacy assign/chat KPI code from dynamic schema code (e.g. MAN-a-B1 → B1). */
export function resolveLegacyKpiAssignCode(schemaOrCode) {
  const code =
    typeof schemaOrCode === "string"
      ? schemaOrCode
      : schemaOrCode?.code || schemaOrCode?.kpi;
  if (!code) return null;
  const parsed = parseLegacyKpiCode(code);
  if (parsed) return parsed;
  if (/^[BESG]\d+$/i.test(String(code))) return String(code).toUpperCase();
  return null;
}

export function normalizeMemberId(memberId) {
  if (memberId == null) return null;
  if (typeof memberId === "object") {
    return String(memberId.id ?? memberId._id ?? "");
  }
  return String(memberId);
}

function coerceLegacyValue(legacyKey, rawValue) {
  if (rawValue == null || rawValue === "") return null;

  if (BOOLEAN_LEGACY_FIELDS.has(legacyKey)) {
    if (rawValue === "y" || rawValue === true || rawValue === 1) return true;
    if (rawValue === "n" || rawValue === false || rawValue === 0) return false;
    return rawValue;
  }

  const n = Number(rawValue);
  return Number.isFinite(n) ? n : rawValue;
}

export function hydrateReportFromDynamicKpis(report) {
  if (!report?.dynamicKPIs) return report;

  const mappedReport = JSON.parse(JSON.stringify(report));

  Object.entries(mappedReport.dynamicKPIs || {}).forEach(([schemaCode, entry]) => {
    const legacyCode = parseLegacyKpiCode(schemaCode);
    const sectionKey = legacyCode ? KPI_CODE_TO_SECTION[legacyCode[0]] : null;
    const fieldMap = legacyCode ? DYNAMIC_TO_LEGACY_FIELD_MAP[legacyCode] : null;
    const codeSection = sectionKey ? mappedReport?.[sectionKey]?.[legacyCode] : null;
    const values = entry?.values || {};

    if (!sectionKey || !fieldMap || !codeSection) return;

    Object.entries(fieldMap).forEach(([legacyKey, dynamicKeys]) => {
      const resolvedDynamicKey = dynamicKeys.find(
        (candidate) => values[candidate] != null && values[candidate] !== ""
      );
      if (!resolvedDynamicKey) return;

      if (!codeSection[legacyKey] || typeof codeSection[legacyKey] !== "object") {
        codeSection[legacyKey] = {};
      }
      codeSection[legacyKey].value = coerceLegacyValue(
        legacyKey,
        values[resolvedDynamicKey]
      );
    });
  });

  return mappedReport;
}
